import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import { formatDisplayName, determineScreamCategory } from '../../../utilities/BL/PersonalNoisemeter';

/**
 * Dibuja un rectángulo con esquinas redondeadas compatible con todos los navegadores.
 */
function drawRoundedRect(ctx, x, y, width, height, radius) {
    if (typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, radius);
    } else {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    }
}

/**
 * Trunca texto si excede el ancho máximo disponible en el Canvas.
 */
function truncateText(ctx, text, maxWidth) {
    if (!text) return '';
    if (ctx.measureText(text).width <= maxWidth) return text;
    let truncated = text;
    while (truncated.length > 3 && ctx.measureText(truncated + '...').width > maxWidth) {
        truncated = truncated.slice(0, -1);
    }
    return truncated + '...';
}

/**
 * Dibuja una cinta tricolor mexicana (Verde, Blanco, Rojo).
 */
function drawMexicanRibbon(ctx, x, y, width, height) {
    const third = width / 3;
    // Verde
    ctx.fillStyle = '#006847';
    ctx.fillRect(x, y, third, height);
    // Blanco
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(x + third, y, third, height);
    // Rojo
    ctx.fillStyle = '#CE1126';
    ctx.fillRect(x + third * 2, y, third, height);
}

/**
 * Modal y Generador de Imágenes para Redes Sociales (HTML5 Canvas 2D)
 * [HU-04] Generador de imágenes para Instagram Stories (9:16) y Feed / Pantalla (16:9).
 */
export const PersonalNoisemeterSocialModal = ({
    isOpen,
    onClose,
    ranking = [],
    kpiStats = null,
    config = null,
    roomId = null
}) => {
    const { showMessage } = useFloatMessage();
    const canvasRef = useRef(null);

    // Formato seleccionado: 'stories' (1080x1920) o 'feed' (1920x1080)
    const [activeTab, setActiveTab] = useState('stories'); // 'stories' | 'feed'

    // Opciones de personalización en vivo
    const [maskNames, setMaskNames] = useState(true);
    const [customEventTitle, setCustomEventTitle] = useState(config?.headerTitle || '¡GRITO MEXICANO!');
    const [customSubtitle, setCustomSubtitle] = useState(config?.headerSubtitle || 'EL GRITO MÁS FUERTE');
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (config?.headerTitle) setCustomEventTitle(config.headerTitle);
        if (config?.headerSubtitle) setCustomSubtitle(config.headerSubtitle);
    }, [config]);

    // Normalizar lista de participantes reales únicamente
    const processedRanking = useMemo(() => {
        if (!ranking || !Array.isArray(ranking) || ranking.length === 0) {
            return [];
        }

        return ranking
            .filter(p => p && !p.isDisqualified && !p.isArchived && (Number(p.decibelsPeak || p.finalScore || p.score || 0) > 0 || p.fullName || p.displayName))
            .sort((a, b) => {
                const scoreA = Number(a.decibelsPeak || a.finalScore || a.score || 0);
                const scoreB = Number(b.decibelsPeak || b.finalScore || b.score || 0);
                return scoreB - scoreA;
            });
    }, [ranking]);

    // Estadísticas globales calculadas
    const stats = useMemo(() => {
        const count = kpiStats?.totalRegistros || (ranking && Array.isArray(ranking) ? ranking.length : processedRanking.length);
        const peak = (processedRanking && processedRanking.length > 0)
            ? Number(processedRanking[0]?.decibelsPeak || processedRanking[0]?.finalScore || 0)
            : 0;
        return {
            totalParticipants: count || 0,
            recordPeak: peak > 0 ? peak.toFixed(1) : '0.0'
        };
    }, [kpiStats, ranking, processedRanking]);

    // =========================================================================
    // MOTOR DE DIBUJO CANVAS 2D: INSTAGRAM STORIES (1080 x 1920 px)
    // =========================================================================
    const renderStoriesCanvas = useCallback((ctx) => {
        const W = 1080;
        const H = 1920;

        ctx.clearRect(0, 0, W, H);

        // 1. FONDO DEGRADADO PATRIO OBSCURO
        const bgGrad = ctx.createLinearGradient(0, 0, W, H);
        bgGrad.addColorStop(0, '#04160d');   // Verde bosque profundo
        bgGrad.addColorStop(0.35, '#0a120f'); // Grafito esmeralda
        bgGrad.addColorStop(0.70, '#120b0e'); // Grafito granate
        bgGrad.addColorStop(1, '#1e0609');   // Rojo sangre profundo
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, W, H);

        // Resplandores radiales ambientales
        const topGlow = ctx.createRadialGradient(200, 200, 50, 200, 200, 550);
        topGlow.addColorStop(0, 'rgba(0, 104, 71, 0.35)');
        topGlow.addColorStop(1, 'rgba(0, 104, 71, 0)');
        ctx.fillStyle = topGlow;
        ctx.fillRect(0, 0, W, H);

        const centerGlow = ctx.createRadialGradient(540, 500, 80, 540, 500, 600);
        centerGlow.addColorStop(0, 'rgba(255, 215, 0, 0.14)');
        centerGlow.addColorStop(1, 'rgba(255, 215, 0, 0)');
        ctx.fillStyle = centerGlow;
        ctx.fillRect(0, 0, W, H);

        const bottomGlow = ctx.createRadialGradient(880, 1700, 80, 880, 1700, 650);
        bottomGlow.addColorStop(0, 'rgba(206, 17, 38, 0.28)');
        bottomGlow.addColorStop(1, 'rgba(206, 17, 38, 0)');
        ctx.fillStyle = bottomGlow;
        ctx.fillRect(0, 0, W, H);

        // Cinta tricolor superior
        drawMexicanRibbon(ctx, 0, 0, W, 14);

        // Patrón sutil de ondas acústicas en el fondo
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
        ctx.lineWidth = 3;
        for (let r = 100; r <= 800; r += 140) {
            ctx.beginPath();
            ctx.arc(540, 520, r, 0, Math.PI * 2);
            ctx.stroke();
        }

        // 2. ENCABEZADO
        // Badge superior
        const badgeText = '🇲🇽 FIESTAS PATRIAS • PODIO OFICIAL 🇲🇽';
        ctx.font = '700 22px "Montserrat", "Segoe UI", sans-serif';
        const badgeWidth = ctx.measureText(badgeText).width + 50;
        const badgeX = (W - badgeWidth) / 2;
        const badgeY = 70;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
        drawRoundedRect(ctx, badgeX, badgeY, badgeWidth, 46, 23);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.20)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = '#E2E8F0';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(badgeText, W / 2, badgeY + 23);

        // Título Principal '¡GRITO MEXICANO!'
        const titleText = customEventTitle || '¡GRITO MEXICANO!';
        ctx.font = '900 78px "Montserrat", "Impact", "Arial Black", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';

        // Sombra de resplandor para el título
        ctx.shadowColor = 'rgba(255, 200, 0, 0.6)';
        ctx.shadowBlur = 28;

        const titleGrad = ctx.createLinearGradient(0, 140, 0, 210);
        titleGrad.addColorStop(0, '#FFF6A9');
        titleGrad.addColorStop(0.5, '#FFD700');
        titleGrad.addColorStop(1, '#FF9900');
        ctx.fillStyle = titleGrad;
        ctx.fillText(titleText, W / 2, 195);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        // Subtítulo
        ctx.font = '800 26px "Montserrat", "Segoe UI", sans-serif';
        ctx.fillStyle = '#00E676';
        ctx.fillText(customSubtitle || 'EL GRITO MÁS FUERTE', W / 2, 240);

        // Separador tricolor
        drawMexicanRibbon(ctx, W / 2 - 140, 265, 280, 5);

        const cardX = 70;
        const cardW = 940;

        if (processedRanking.length === 0) {
            // ==================== ESTADO VACÍO (SIN PARTICIPANTES) ====================
            const emptyCardY = 440;
            const emptyCardH = 760;

            ctx.shadowColor = 'rgba(0, 104, 71, 0.35)';
            ctx.shadowBlur = 30;

            const emptyGrad = ctx.createLinearGradient(cardX, emptyCardY, cardX + cardW, emptyCardY + emptyCardH);
            emptyGrad.addColorStop(0, 'rgba(0, 104, 71, 0.20)');
            emptyGrad.addColorStop(1, 'rgba(12, 20, 16, 0.95)');
            ctx.fillStyle = emptyGrad;
            drawRoundedRect(ctx, cardX, emptyCardY, cardW, emptyCardH, 32);
            ctx.fill();

            ctx.strokeStyle = 'rgba(0, 230, 118, 0.4)';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            ctx.font = '900 110px "Montserrat", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('🏆', W / 2, emptyCardY + 160);

            ctx.font = '900 44px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFD700';
            ctx.fillText('¡AÚN NO HAY GRITOS REGISTRADOS!', W / 2, emptyCardY + 310);

            ctx.font = '700 28px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText('Escanea el código QR para participar en la dinámica', W / 2, emptyCardY + 380);

            ctx.font = '600 22px "Montserrat", sans-serif';
            ctx.fillStyle = '#CBD5E0';
            ctx.fillText('El podio oficial se actualizará conforme jueguen los participantes.', W / 2, emptyCardY + 450);

            drawMexicanRibbon(ctx, W / 2 - 130, emptyCardY + 540, 260, 6);
        } else {
            // ==================== TARJETAS DEL PODIO TOP 3 (REALES) ====================

            // --- 🥇 1ER LUGAR (ORO) ---
            const first = processedRanking[0];
            const firstName = formatDisplayName(first.fullName || first.displayName || 'Campeón', maskNames);
            const firstTicket = first.participationTicket || first.ticket || 'GM-0001';
            const firstDb = Number(first.decibelsPeak || first.finalScore || 0).toFixed(1);
            const firstCategory = first.category || determineScreamCategory(firstDb);

            const card1Y = 320;
            const card1H = 260;

            ctx.shadowColor = 'rgba(255, 215, 0, 0.45)';
            ctx.shadowBlur = 30;

            const goldGrad = ctx.createLinearGradient(cardX, card1Y, cardX + cardW, card1Y + card1H);
            goldGrad.addColorStop(0, 'rgba(255, 215, 0, 0.22)');
            goldGrad.addColorStop(1, 'rgba(32, 26, 12, 0.94)');
            ctx.fillStyle = goldGrad;
            drawRoundedRect(ctx, cardX, card1Y, cardW, card1H, 28);
            ctx.fill();

            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 3.5;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Medalla 1º
            const med1X = cardX + 75;
            const med1Y = card1Y + card1H / 2;
            const med1Grad = ctx.createLinearGradient(med1X - 45, med1Y - 45, med1X + 45, med1Y + 45);
            med1Grad.addColorStop(0, '#FFE875');
            med1Grad.addColorStop(1, '#D4A000');
            ctx.fillStyle = med1Grad;
            ctx.beginPath();
            ctx.arc(med1X, med1Y, 48, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFF';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.font = '900 36px "Montserrat", sans-serif';
            ctx.fillStyle = '#1A1300';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('1º', med1X, med1Y);

            // Datos del 1er Lugar
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';

            ctx.font = '800 20px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFD700';
            ctx.fillText('👑 1º LUGAR • CAMPEÓN OFICIAL', cardX + 145, card1Y + 58);

            ctx.font = '900 42px "Montserrat", "Segoe UI", sans-serif';
            ctx.fillStyle = '#FFFFFF';
            const truncatedName1 = truncateText(ctx, firstName, 440);
            ctx.fillText(truncatedName1, cardX + 145, card1Y + 115);

            ctx.font = '700 20px "Courier New", monospace';
            const t1Text = `TICKET: ${firstTicket}`;
            const t1Width = ctx.measureText(t1Text).width + 24;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
            drawRoundedRect(ctx, cardX + 145, card1Y + 145, t1Width, 34, 10);
            ctx.fill();
            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.fillStyle = '#FFD700';
            ctx.fillText(t1Text, cardX + 157, card1Y + 168);

            ctx.font = '700 18px "Montserrat", sans-serif';
            const cat1Text = firstCategory;
            const cat1Width = ctx.measureText(cat1Text).width + 24;
            const cat1X = cardX + 145 + t1Width + 15;
            ctx.fillStyle = '#006847';
            drawRoundedRect(ctx, cat1X, card1Y + 145, cat1Width, 34, 10);
            ctx.fill();
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText(cat1Text, cat1X + 12, card1Y + 168);

            // Decibeles 1er Lugar
            ctx.textAlign = 'right';
            ctx.font = '900 70px "Montserrat", "Impact", sans-serif';
            ctx.fillStyle = '#FFD700';
            ctx.fillText(`${firstDb}`, cardX + cardW - 40, card1Y + 125);

            ctx.font = '800 28px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFE57F';
            ctx.fillText('PICO', cardX + cardW - 40, card1Y + 165);

            // --- 🥈 2DO LUGAR (PLATA) ---
            const second = processedRanking[1];
            const isSecondAvail = !!second;
            const secondName = isSecondAvail ? formatDisplayName(second.fullName || second.displayName || 'Subcampeón', maskNames) : 'Disponible';
            const secondTicket = isSecondAvail ? (second.participationTicket || second.ticket || 'GM-0002') : '---';
            const secondDb = isSecondAvail ? Number(second.decibelsPeak || second.finalScore || 0).toFixed(1) : '-';
            const secondCategory = isSecondAvail ? (second.category || determineScreamCategory(secondDb)) : 'Sin registro';

            const card2Y = 610;
            const card2H = 210;

            ctx.shadowColor = 'rgba(226, 232, 240, 0.35)';
            ctx.shadowBlur = 20;

            const silverGrad = ctx.createLinearGradient(cardX, card2Y, cardX + cardW, card2Y + card2H);
            silverGrad.addColorStop(0, isSecondAvail ? 'rgba(226, 232, 240, 0.16)' : 'rgba(226, 232, 240, 0.05)');
            silverGrad.addColorStop(1, 'rgba(20, 24, 28, 0.92)');
            ctx.fillStyle = silverGrad;
            drawRoundedRect(ctx, cardX, card2Y, cardW, card2H, 24);
            ctx.fill();

            ctx.strokeStyle = isSecondAvail ? '#CBD5E0' : 'rgba(203, 213, 224, 0.3)';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Medalla 2º
            const med2X = cardX + 70;
            const med2Y = card2Y + card2H / 2;
            const med2Grad = ctx.createLinearGradient(med2X - 40, med2Y - 40, med2X + 40, med2Y + 40);
            med2Grad.addColorStop(0, '#FFFFFF');
            med2Grad.addColorStop(1, '#A0AEC0');
            ctx.fillStyle = med2Grad;
            ctx.beginPath();
            ctx.arc(med2X, med2Y, 42, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFF';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.font = '900 32px "Montserrat", sans-serif';
            ctx.fillStyle = '#1A202C';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('2º', med2X, med2Y);

            // Datos 2º
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.font = '800 18px "Montserrat", sans-serif';
            ctx.fillStyle = '#CBD5E0';
            ctx.fillText('🥈 2º LUGAR', cardX + 135, card2Y + 50);

            ctx.font = '900 36px "Montserrat", sans-serif';
            ctx.fillStyle = isSecondAvail ? '#FFFFFF' : '#718096';
            ctx.fillText(truncateText(ctx, secondName, 440), cardX + 135, card2Y + 100);

            ctx.font = '700 18px "Courier New", monospace';
            ctx.fillStyle = isSecondAvail ? '#A0AEC0' : '#4A5568';
            ctx.fillText(`TICKET: ${secondTicket}  •  ${secondCategory}`, cardX + 135, card2Y + 145);

            // Decibeles 2º
            ctx.textAlign = 'right';
            ctx.font = '900 58px "Montserrat", sans-serif';
            ctx.fillStyle = isSecondAvail ? '#E2E8F0' : '#718096';
            ctx.fillText(isSecondAvail ? `${secondDb}` : '-', cardX + cardW - 40, card2Y + 105);

            ctx.font = '800 22px "Montserrat", sans-serif';
            ctx.fillStyle = '#A0AEC0';
            ctx.fillText('PICO', cardX + cardW - 40, card2Y + 142);

            // --- 🥉 3ER LUGAR (BRONCE) ---
            const third = processedRanking[2];
            const isThirdAvail = !!third;
            const thirdName = isThirdAvail ? formatDisplayName(third.fullName || third.displayName || 'Tercer Lugar', maskNames) : 'Disponible';
            const thirdTicket = isThirdAvail ? (third.participationTicket || third.ticket || 'GM-0003') : '---';
            const thirdDb = isThirdAvail ? Number(third.decibelsPeak || third.finalScore || 0).toFixed(1) : '-';
            const thirdCategory = isThirdAvail ? (third.category || determineScreamCategory(thirdDb)) : 'Sin registro';

            const card3Y = 845;
            const card3H = 210;

            ctx.shadowColor = 'rgba(205, 127, 50, 0.35)';
            ctx.shadowBlur = 20;

            const bronzeGrad = ctx.createLinearGradient(cardX, card3Y, cardX + cardW, card3Y + card3H);
            bronzeGrad.addColorStop(0, isThirdAvail ? 'rgba(205, 127, 50, 0.16)' : 'rgba(205, 127, 50, 0.05)');
            bronzeGrad.addColorStop(1, 'rgba(28, 18, 12, 0.92)');
            ctx.fillStyle = bronzeGrad;
            drawRoundedRect(ctx, cardX, card3Y, cardW, card3H, 24);
            ctx.fill();

            ctx.strokeStyle = isThirdAvail ? '#CD7F32' : 'rgba(205, 127, 50, 0.3)';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Medalla 3º
            const med3X = cardX + 70;
            const med3Y = card3Y + card3H / 2;
            const med3Grad = ctx.createLinearGradient(med3X - 40, med3Y - 40, med3X + 40, med3Y + 40);
            med3Grad.addColorStop(0, '#E69C58');
            med3Grad.addColorStop(1, '#9C5316');
            ctx.fillStyle = med3Grad;
            ctx.beginPath();
            ctx.arc(med3X, med3Y, 42, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFF';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.font = '900 32px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFFFFF';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('3º', med3X, med3Y);

            // Datos 3º
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.font = '800 18px "Montserrat", sans-serif';
            ctx.fillStyle = '#ED8936';
            ctx.fillText('🥉 3º LUGAR', cardX + 135, card3Y + 42);

            ctx.font = '900 36px "Montserrat", sans-serif';
            ctx.fillStyle = isThirdAvail ? '#FFFFFF' : '#718096';
            ctx.fillText(truncateText(ctx, thirdName, 440), cardX + 135, card3Y + 100);

            ctx.font = '700 18px "Courier New", monospace';
            ctx.fillStyle = isThirdAvail ? '#CBD5E0' : '#4A5568';
            ctx.fillText(`TICKET: ${thirdTicket}  •  ${thirdCategory}`, cardX + 135, card3Y + 145);

            // Decibeles 3º
            ctx.textAlign = 'right';
            ctx.font = '900 58px "Montserrat", sans-serif';
            ctx.fillStyle = isThirdAvail ? '#ED8936' : '#718096';
            ctx.fillText(isThirdAvail ? `${thirdDb}` : '-', cardX + cardW - 40, card3Y + 105);

            ctx.font = '800 22px "Montserrat", sans-serif';
            ctx.fillStyle = '#CBD5E0';
            ctx.fillText('PICO', cardX + cardW - 40, card3Y + 142);

            // 4. BLOQUE SECUNDARIO: POSICIONES 4 A 6
            const header4to6Y = 1090;
            ctx.textAlign = 'left';
            ctx.font = '800 22px "Montserrat", sans-serif';
            ctx.fillStyle = '#68D391';
            ctx.fillText('⚡ TOP 4 A 6 • POSICIONES DESTACADAS', cardX, header4to6Y);

            const subItems = processedRanking.slice(3, 6);
            const startY = 1115;
            const rowH = 92;
            const rowGap = 16;

            for (let i = 0; i < 3; i++) {
                const p = subItems[i];
                const isAvail = !!p;
                const pName = isAvail ? formatDisplayName(p.fullName || p.displayName, maskNames) : 'Posición disponible';
                const pTicket = isAvail ? (p.participationTicket || p.ticket || '---') : '---';
                const pDb = isAvail ? `${Number(p.decibelsPeak || p.finalScore || 0).toFixed(1)}` : '-';
                const rowY = startY + i * (rowH + rowGap);

                ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
                drawRoundedRect(ctx, cardX, rowY, cardW, rowH, 18);
                ctx.fill();
                ctx.strokeStyle = isAvail ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)';
                ctx.lineWidth = 1.5;
                ctx.stroke();

                ctx.fillStyle = 'rgba(255, 255, 255, 0.10)';
                drawRoundedRect(ctx, cardX + 20, rowY + 18, 56, 56, 14);
                ctx.fill();

                ctx.font = '900 24px "Montserrat", sans-serif';
                ctx.fillStyle = '#E2E8F0';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(`#${i + 4}`, cardX + 48, rowY + 46);

                ctx.textAlign = 'left';
                ctx.textBaseline = 'alphabetic';
                ctx.font = '800 26px "Montserrat", sans-serif';
                ctx.fillStyle = isAvail ? '#FFFFFF' : '#718096';
                ctx.fillText(truncateText(ctx, pName, 480), cardX + 96, rowY + 44);

                ctx.font = '600 18px "Courier New", monospace';
                ctx.fillStyle = isAvail ? '#A0AEC0' : '#4A5568';
                ctx.fillText(`TICKET: ${pTicket}`, cardX + 96, rowY + 72);

                ctx.textAlign = 'right';
                ctx.font = '900 36px "Montserrat", sans-serif';
                ctx.fillStyle = isAvail ? '#48BB78' : '#718096';
                ctx.fillText(pDb, cardX + cardW - 30, rowY + 56);
            }
        }

        // 5. PIE DE PÁGINA Y BRANDING
        const footerY = 1490;

        // Separador tricolor
        drawMexicanRibbon(ctx, cardX, footerY, cardW, 4);

        // Tarjeta resumen estadísticas
        const statsY = footerY + 25;
        const statsH = 90;
        ctx.fillStyle = 'rgba(0, 104, 71, 0.25)';
        drawRoundedRect(ctx, cardX, statsY, cardW, statsH, 20);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0, 230, 118, 0.4)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '700 22px "Montserrat", sans-serif';
        ctx.fillStyle = '#E2E8F0';
        ctx.fillText(
            `👥 Total Participantes: ${stats.totalParticipants}   |   ⚡ Récord del Evento: ${stats.recordPeak}`,
            W / 2,
            statsY + statsH / 2
        );

        // Fecha oficial
        const dateStr = new Date().toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        ctx.font = '600 20px "Montserrat", sans-serif';
        ctx.fillStyle = '#A0AEC0';
        ctx.fillText(`📅 ${dateStr} • Dinámica Oficial Fiestas Patrias`, W / 2, statsY + statsH + 50);

        // Logo / Branding Interaktia
        ctx.font = '900 32px "Montserrat", sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText('XOLOS', W / 2, statsY + statsH + 110);

        ctx.font = '600 18px "Montserrat", sans-serif';
        ctx.fillStyle = '#00E676';
        ctx.fillText('TECNOLOGÍA INTERACTIVA PARA EVENTOS', W / 2, statsY + statsH + 145);

        // Cinta tricolor inferior
        drawMexicanRibbon(ctx, 0, H - 14, W, 14);
    }, [processedRanking, maskNames, customEventTitle, customSubtitle, stats]);

    // =========================================================================
    // MOTOR DE DIBUJO CANVAS 2D: FEED / PANTALLA (1920 x 1080 px)
    // =========================================================================
    const renderFeedCanvas = useCallback((ctx) => {
        const W = 1920;
        const H = 1080;

        ctx.clearRect(0, 0, W, H);

        // 1. FONDO HORIZONTAL DEGRADADO PATRIO
        const bgGrad = ctx.createLinearGradient(0, 0, W, H);
        bgGrad.addColorStop(0, '#04160d');    // Verde profundo
        bgGrad.addColorStop(0.5, '#0b100e');  // Centro carbón
        bgGrad.addColorStop(1, '#1e0609');    // Rojo sangre profundo
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, W, H);

        // Resplandores ambientales
        const leftGlow = ctx.createRadialGradient(300, 300, 60, 300, 300, 650);
        leftGlow.addColorStop(0, 'rgba(0, 104, 71, 0.32)');
        leftGlow.addColorStop(1, 'rgba(0, 104, 71, 0)');
        ctx.fillStyle = leftGlow;
        ctx.fillRect(0, 0, W, H);

        const rightGlow = ctx.createRadialGradient(1600, 600, 80, 1600, 600, 700);
        rightGlow.addColorStop(0, 'rgba(206, 17, 38, 0.25)');
        rightGlow.addColorStop(1, 'rgba(206, 17, 38, 0)');
        ctx.fillStyle = rightGlow;
        ctx.fillRect(0, 0, W, H);

        // Cintas tricolor superior e inferior
        drawMexicanRibbon(ctx, 0, 0, W, 10);
        drawMexicanRibbon(ctx, 0, H - 10, W, 10);

        // 2. BARRA DE ENCABEZADO HORIZONTAL
        const headerY = 50;

        // Título y subtítulo a la izquierda
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';

        // Badge superior
        ctx.font = '800 16px "Montserrat", sans-serif';
        ctx.fillStyle = '#00E676';
        ctx.fillText('🇲🇽 FIESTAS PATRIAS • CLASIFICACIÓN OFICIAL', 70, headerY + 15);

        // Título Principal
        const titleText = customEventTitle || '¡GRITO MEXICANO!';
        ctx.font = '900 52px "Montserrat", "Impact", sans-serif';
        ctx.shadowColor = 'rgba(255, 200, 0, 0.5)';
        ctx.shadowBlur = 20;

        const titleGrad = ctx.createLinearGradient(70, headerY + 30, 70, headerY + 80);
        titleGrad.addColorStop(0, '#FFF6A9');
        titleGrad.addColorStop(1, '#FFD700');
        ctx.fillStyle = titleGrad;
        ctx.fillText(titleText, 70, headerY + 72);

        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        ctx.font = '800 20px "Montserrat", sans-serif';
        ctx.fillStyle = '#E2E8F0';
        ctx.fillText(customSubtitle || 'EL GRITO MÁS FUERTE', 70, headerY + 105);

        // Tarjetas y datos a la derecha
        ctx.textAlign = 'right';

        // Logo Interaktia
        ctx.font = '900 28px "Montserrat", sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText('XOLOS', W - 70, headerY + 35);

        // Stats badge
        ctx.font = '700 18px "Montserrat", sans-serif';
        ctx.fillStyle = '#CBD5E0';
        ctx.fillText(
            `👥 ${stats.totalParticipants} Participantes   |   ⚡ Récord: ${stats.recordPeak}`,
            W - 70,
            headerY + 70
        );

        const dateStr = new Date().toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        ctx.font = '500 16px "Montserrat", sans-serif';
        ctx.fillStyle = '#A0AEC0';
        ctx.fillText(`📅 ${dateStr}`, W - 70, headerY + 100);

        // Separador tricolor horizontal
        drawMexicanRibbon(ctx, 70, 175, W - 140, 4);

        if (processedRanking.length === 0) {
            // ==================== ESTADO VACÍO (SIN PARTICIPANTES) ====================
            const emptyCardX = 360;
            const emptyCardY = 240;
            const emptyCardW = 1200;
            const emptyCardH = 680;

            ctx.shadowColor = 'rgba(0, 104, 71, 0.35)';
            ctx.shadowBlur = 30;

            const emptyGrad = ctx.createLinearGradient(emptyCardX, emptyCardY, emptyCardX + emptyCardW, emptyCardY + emptyCardH);
            emptyGrad.addColorStop(0, 'rgba(0, 104, 71, 0.20)');
            emptyGrad.addColorStop(1, 'rgba(12, 20, 16, 0.95)');
            ctx.fillStyle = emptyGrad;
            drawRoundedRect(ctx, emptyCardX, emptyCardY, emptyCardW, emptyCardH, 28);
            ctx.fill();

            ctx.strokeStyle = 'rgba(0, 230, 118, 0.4)';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            ctx.font = '900 110px "Montserrat", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('🏆', W / 2, emptyCardY + 150);

            ctx.font = '900 48px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFD700';
            ctx.fillText('¡AÚN NO HAY GRITOS REGISTRADOS!', W / 2, emptyCardY + 290);

            ctx.font = '700 28px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText('Escanea el código QR para participar en la dinámica', W / 2, emptyCardY + 365);

            ctx.font = '600 22px "Montserrat", sans-serif';
            ctx.fillStyle = '#CBD5E0';
            ctx.fillText('El podio oficial se actualizará en tiempo real conforme jueguen los aficionados.', W / 2, emptyCardY + 430);

            drawMexicanRibbon(ctx, W / 2 - 160, emptyCardY + 520, 320, 6);
        } else {
            // 3. COMPOSICIÓN DE DOS COLUMNAS
            const col1X = 70;
            const col1W = 840;
            const col2X = 960;
            const col2W = 890;
            const bodyY = 205;

            // ==================== COLUMNA IZQUIERDA: TOP 3 PODIO ====================
            ctx.textAlign = 'left';
            ctx.font = '900 24px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFD700';
            ctx.fillText('🏆 PODIO DE HONOR (TOP 3)', col1X, bodyY + 25);

            // --- 🥇 1ER LUGAR (ORO) ---
            const first = processedRanking[0];
            const firstName = formatDisplayName(first.fullName || first.displayName || 'Campeón', maskNames);
            const firstTicket = first.participationTicket || first.ticket || 'GM-0001';
            const firstDb = Number(first.decibelsPeak || first.finalScore || 0).toFixed(1);
            const firstCategory = first.category || determineScreamCategory(firstDb);

            const c1Y = bodyY + 45;
            const c1H = 220;

            ctx.shadowColor = 'rgba(255, 215, 0, 0.4)';
            ctx.shadowBlur = 24;

            const goldGrad = ctx.createLinearGradient(col1X, c1Y, col1X + col1W, c1Y + c1H);
            goldGrad.addColorStop(0, 'rgba(255, 215, 0, 0.22)');
            goldGrad.addColorStop(1, 'rgba(32, 26, 12, 0.94)');
            ctx.fillStyle = goldGrad;
            drawRoundedRect(ctx, col1X, c1Y, col1W, c1H, 22);
            ctx.fill();

            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 3;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Medalla 1
            const m1X = col1X + 65;
            const m1Y = c1Y + c1H / 2;
            const m1Grad = ctx.createLinearGradient(m1X - 38, m1Y - 38, m1X + 38, m1Y + 38);
            m1Grad.addColorStop(0, '#FFE875');
            m1Grad.addColorStop(1, '#D4A000');
            ctx.fillStyle = m1Grad;
            ctx.beginPath();
            ctx.arc(m1X, m1Y, 42, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFF';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.font = '900 32px "Montserrat", sans-serif';
            ctx.fillStyle = '#1A1300';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('1º', m1X, m1Y);

            // Datos 1
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.font = '800 17px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFD700';
            ctx.fillText('👑 1º LUGAR • CAMPEÓN', col1X + 130, c1Y + 48);

            ctx.font = '900 36px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText(truncateText(ctx, firstName, 420), col1X + 130, c1Y + 98);

            ctx.font = '700 17px "Courier New", monospace';
            ctx.fillStyle = '#FFE066';
            ctx.fillText(`TICKET: ${firstTicket}  •  ${firstCategory}`, col1X + 130, c1Y + 140);

            // Decibeles 1
            ctx.textAlign = 'right';
            ctx.font = '900 62px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFD700';
            ctx.fillText(`${firstDb}`, col1X + col1W - 30, c1Y + 105);

            ctx.font = '800 20px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFE57F';
            ctx.fillText('Máx.', col1X + col1W - 30, c1Y + 145);

            // --- 🥈 2DO LUGAR (PLATA) ---
            const second = processedRanking[1];
            const isSecondAvail = !!second;
            const secondName = isSecondAvail ? formatDisplayName(second.fullName || second.displayName || 'Subcampeón', maskNames) : 'Disponible';
            const secondTicket = isSecondAvail ? (second.participationTicket || second.ticket || 'GM-0002') : '---';
            const secondDb = isSecondAvail ? Number(second.decibelsPeak || second.finalScore || 0).toFixed(1) : '-';
            const secondCategory = isSecondAvail ? (second.category || determineScreamCategory(secondDb)) : 'Sin registro';

            const c2Y = c1Y + c1H + 18;
            const c2H = 175;

            ctx.shadowColor = 'rgba(226, 232, 240, 0.3)';
            ctx.shadowBlur = 18;

            const silverGrad = ctx.createLinearGradient(col1X, c2Y, col1X + col1W, c2Y + c2H);
            silverGrad.addColorStop(0, isSecondAvail ? 'rgba(226, 232, 240, 0.15)' : 'rgba(226, 232, 240, 0.05)');
            silverGrad.addColorStop(1, 'rgba(20, 24, 28, 0.92)');
            ctx.fillStyle = silverGrad;
            drawRoundedRect(ctx, col1X, c2Y, col1W, c2H, 20);
            ctx.fill();

            ctx.strokeStyle = isSecondAvail ? '#CBD5E0' : 'rgba(203, 213, 224, 0.3)';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Medalla 2
            const m2X = col1X + 60;
            const m2Y = c2Y + c2H / 2;
            ctx.fillStyle = isSecondAvail ? '#A0AEC0' : '#4A5568';
            ctx.beginPath();
            ctx.arc(m2X, m2Y, 36, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFF';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.font = '900 26px "Montserrat", sans-serif';
            ctx.fillStyle = isSecondAvail ? '#1A202C' : '#CBD5E0';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('2º', m2X, m2Y);

            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.font = '800 15px "Montserrat", sans-serif';
            ctx.fillStyle = isSecondAvail ? '#CBD5E0' : '#718096';
            ctx.fillText('🥈 2º LUGAR', col1X + 115, c2Y + 42);

            ctx.font = '900 28px "Montserrat", sans-serif';
            ctx.fillStyle = isSecondAvail ? '#FFFFFF' : '#718096';
            ctx.fillText(truncateText(ctx, secondName, 420), col1X + 115, c2Y + 84);

            ctx.font = '700 16px "Courier New", monospace';
            ctx.fillStyle = isSecondAvail ? '#A0AEC0' : '#4A5568';
            ctx.fillText(`TICKET: ${secondTicket}  •  ${secondCategory}`, col1X + 115, c2Y + 120);

            ctx.textAlign = 'right';
            ctx.font = '900 50px "Montserrat", sans-serif';
            ctx.fillStyle = isSecondAvail ? '#E2E8F0' : '#718096';
            ctx.fillText(isSecondAvail ? `${secondDb}` : '-', col1X + col1W - 30, c2Y + 90);

            ctx.font = '800 18px "Montserrat", sans-serif';
            ctx.fillStyle = isSecondAvail ? '#A0AEC0' : '#4A5568';
            ctx.fillText('PICO', col1X + col1W - 30, c2Y + 124);

            // --- 🥉 3ER LUGAR (BRONCE) ---
            const third = processedRanking[2];
            const isThirdAvail = !!third;
            const thirdName = isThirdAvail ? formatDisplayName(third.fullName || third.displayName || 'Tercero', maskNames) : 'Disponible';
            const thirdTicket = isThirdAvail ? (third.participationTicket || third.ticket || 'GM-0003') : '---';
            const thirdDb = isThirdAvail ? Number(third.decibelsPeak || third.finalScore || 0).toFixed(1) : '-';
            const thirdCategory = isThirdAvail ? (third.category || determineScreamCategory(thirdDb)) : 'Sin registro';

            const c3Y = c2Y + c2H + 18;
            const c3H = 175;

            ctx.shadowColor = 'rgba(205, 127, 50, 0.3)';
            ctx.shadowBlur = 18;

            const bronzeGrad = ctx.createLinearGradient(col1X, c3Y, col1X + col1W, c3Y + c3H);
            bronzeGrad.addColorStop(0, isThirdAvail ? 'rgba(205, 127, 50, 0.15)' : 'rgba(205, 127, 50, 0.05)');
            bronzeGrad.addColorStop(1, 'rgba(28, 18, 12, 0.92)');
            ctx.fillStyle = bronzeGrad;
            drawRoundedRect(ctx, col1X, c3Y, col1W, c3H, 20);
            ctx.fill();

            ctx.strokeStyle = isThirdAvail ? '#CD7F32' : 'rgba(205, 127, 50, 0.3)';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Medalla 3
            const m3X = col1X + 60;
            const m3Y = c3Y + c3H / 2;
            ctx.fillStyle = isThirdAvail ? '#9C5316' : '#4A301E';
            ctx.beginPath();
            ctx.arc(m3X, m3Y, 36, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFF';
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.font = '900 26px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFFFFF';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('3º', m3X, m3Y);

            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.font = '800 15px "Montserrat", sans-serif';
            ctx.fillStyle = isThirdAvail ? '#ED8936' : '#718096';
            ctx.fillText('🥉 3º LUGAR', col1X + 115, c3Y + 42);

            ctx.font = '900 28px "Montserrat", sans-serif';
            ctx.fillStyle = isThirdAvail ? '#FFFFFF' : '#718096';
            ctx.fillText(truncateText(ctx, thirdName, 420), col1X + 115, c3Y + 84);

            ctx.font = '700 16px "Courier New", monospace';
            ctx.fillStyle = isThirdAvail ? '#CBD5E0' : '#4A5568';
            ctx.fillText(`TICKET: ${thirdTicket}  •  ${thirdCategory}`, col1X + 115, c3Y + 120);

            ctx.textAlign = 'right';
            ctx.font = '900 50px "Montserrat", sans-serif';
            ctx.fillStyle = isThirdAvail ? '#ED8936' : '#718096';
            ctx.fillText(isThirdAvail ? `${thirdDb}` : '-', col1X + col1W - 30, c3Y + 90);

            ctx.font = '800 18px "Montserrat", sans-serif';
            ctx.fillStyle = isThirdAvail ? '#CBD5E0' : '#4A5568';
            ctx.fillText('PICO', col1X + col1W - 30, c3Y + 124);

            // Banner inferior de la columna izquierda
            const bannerY = c3Y + c3H + 18;
            const bannerH = 70;
            ctx.fillStyle = 'rgba(0, 104, 71, 0.3)';
            drawRoundedRect(ctx, col1X, bannerY, col1W, bannerH, 16);
            ctx.fill();
            ctx.strokeStyle = '#00E676';
            ctx.lineWidth = 1.5;
            ctx.stroke();

            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = '800 19px "Montserrat", sans-serif';
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText('🇲🇽 ¡FELICIDADES A LOS GANADORES DEL GRITO MÁS POTENTE! 🇲🇽', col1X + col1W / 2, bannerY + bannerH / 2);

            // ==================== COLUMNA DERECHA: TABLA GENERAL (4 A 10) ====================
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.font = '900 24px "Montserrat", sans-serif';
            ctx.fillStyle = '#63B3ED';
            ctx.fillText('📊 TABLA DE CLASIFICACIÓN (POSICIONES 4 A 10)', col2X, bodyY + 25);

            // Encabezado de la tabla
            const thY = bodyY + 45;
            const thH = 44;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
            drawRoundedRect(ctx, col2X, thY, col2W, thH, 12);
            ctx.fill();

            ctx.font = '800 15px "Montserrat", sans-serif';
            ctx.fillStyle = '#A0AEC0';
            ctx.fillText('POS', col2X + 25, thY + 28);
            ctx.fillText('PARTICIPANTE', col2X + 90, thY + 28);
            ctx.fillText('TICKET', col2X + 380, thY + 28);
            ctx.fillText('CATEGORÍA', col2X + 540, thY + 28);
            ctx.textAlign = 'right';
            ctx.fillText('MÁX.', col2X + col2W - 25, thY + 28);

            // 7 Filas (Posiciones 4 a 10)
            const tableStartY = thY + thH + 12;
            const rowH = 75;
            const rowGap = 10;

            for (let i = 0; i < 7; i++) {
                const p = processedRanking[3 + i];
                const isAvail = !!p;
                const pName = isAvail ? formatDisplayName(p.fullName || p.displayName || `Participante #${i + 4}`, maskNames) : 'Disponible';
                const pTicket = isAvail ? (p.participationTicket || p.ticket || `GM-10${40 + i}`) : '---';
                const pDb = isAvail ? Number(p.decibelsPeak || p.finalScore || 0).toFixed(1) : '-';
                const pCategory = isAvail ? (p.category || determineScreamCategory(pDb)) : '---';

                const rY = tableStartY + i * (rowH + rowGap);

                // Fondo de fila
                ctx.fillStyle = i % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.03)';
                drawRoundedRect(ctx, col2X, rY, col2W, rowH, 14);
                ctx.fill();
                ctx.strokeStyle = isAvail ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.03)';
                ctx.lineWidth = 1;
                ctx.stroke();

                // Badge posición
                ctx.fillStyle = isAvail ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)';
                drawRoundedRect(ctx, col2X + 16, rY + 16, 44, 44, 10);
                ctx.fill();

                ctx.font = '900 20px "Montserrat", sans-serif';
                ctx.fillStyle = isAvail ? '#E2E8F0' : '#4A5568';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(`#${i + 4}`, col2X + 38, rY + 38);

                // Nombre
                ctx.textAlign = 'left';
                ctx.textBaseline = 'alphabetic';
                ctx.font = '800 21px "Montserrat", sans-serif';
                ctx.fillStyle = isAvail ? '#FFFFFF' : '#718096';
                ctx.fillText(truncateText(ctx, pName, 260), col2X + 90, rY + 45);

                // Ticket
                ctx.font = '700 16px "Courier New", monospace';
                ctx.fillStyle = isAvail ? '#CBD5E0' : '#4A5568';
                ctx.fillText(pTicket, col2X + 380, rY + 45);

                // Categoría
                ctx.font = '600 16px "Montserrat", sans-serif';
                ctx.fillStyle = isAvail ? '#A0AEC0' : '#4A5568';
                ctx.fillText(pCategory, col2X + 540, rY + 45);

                // dB
                ctx.textAlign = 'right';
                ctx.font = '900 26px "Montserrat", sans-serif';
                ctx.fillStyle = isAvail ? '#00E676' : '#718096';
                ctx.fillText(isAvail ? `${pDb}` : '-', col2X + col2W - 25, rY + 46);
            }
        }

        // Footer institucional
        ctx.textAlign = 'center';
        ctx.font = '600 15px "Montserrat", sans-serif';
        ctx.fillStyle = '#CBD5E0';
        ctx.fillText('Powered by Interaktia • Dinámica Grito Mexicano', W / 2, H - 24);
    }, [processedRanking, maskNames, customEventTitle, customSubtitle, stats]);

    // =========================================================================
    // EFECTO DE RE-RENDERIZADO REACTIVO DEL CANVAS
    // =========================================================================
    useEffect(() => {
        if (!isOpen) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        if (activeTab === 'stories') {
            canvas.width = 1080;
            canvas.height = 1920;
            renderStoriesCanvas(ctx);
        } else {
            canvas.width = 1920;
            canvas.height = 1080;
            renderFeedCanvas(ctx);
        }
    }, [isOpen, activeTab, renderStoriesCanvas, renderFeedCanvas]);

    // =========================================================================
    // DESCARGAR IMAGEN PNG
    // =========================================================================
    const handleDownloadPng = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        setIsExporting(true);
        try {
            const dataUrl = canvas.toDataURL('image/png', 1.0);
            const dateSuffix = new Date().toISOString().slice(0, 10);
            const filename = activeTab === 'stories'
                ? `grito-mexicano-podio-stories-1080x1920-${dateSuffix}.png`
                : `grito-mexicano-podio-feed-1920x1080-${dateSuffix}.png`;

            const link = document.createElement('a');
            link.download = filename;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: `¡Imagen ${activeTab === 'stories' ? 'Stories (9:16)' : 'Feed (16:9)'} descargada exitosamente en alta definición!`
            });
        } catch (err) {
            console.error('Error al exportar imagen PNG:', err);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al generar o descargar la imagen PNG.'
            });
        } finally {
            setIsExporting(false);
        }
    };

    // =========================================================================
    // COPIAR IMAGEN AL PORTAPAPELES
    // =========================================================================
    const handleCopyToClipboard = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
            showMessage({
                type: MESSAGE_TYPE.INFO,
                text: 'La función de copiado directo no está soportada en este navegador. Usa el botón "Descargar Imagen PNG".'
            });
            return;
        }

        try {
            canvas.toBlob(async (blob) => {
                if (!blob) return;
                try {
                    await navigator.clipboard.write([
                        new ClipboardItem({ 'image/png': blob })
                    ]);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: '¡Imagen copiada al portapapeles! Puedes pegarla directamente en WhatsApp, Slack o redes.'
                    });
                } catch (writeErr) {
                    showMessage({
                        type: MESSAGE_TYPE.INFO,
                        text: 'No se pudo copiar directamente al portapapeles. Usa el botón Descargar Imagen PNG.'
                    });
                }
            }, 'image/png');
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al procesar el copiado al portapapeles.'
            });
        }
    };

    if (!isOpen) return null;

    const hasRealData = processedRanking && processedRanking.length > 0;

    return (
        <>
            {/* Backdrop con Blur */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.82)',
                    zIndex: 99998,
                    backdropFilter: 'blur(6px)'
                }}
            />

            {/* Contenedor del Modal */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '96%',
                    maxWidth: '1240px',
                    maxHeight: '94vh',
                    zIndex: 99999,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white d-flex flex-column" style={{ maxHeight: '94vh' }}>
                    {/* ENCABEZADO DEL MODAL */}
                    <div
                        className="p-3 px-4 d-flex justify-content-between align-items-center text-white flex-shrink-0"
                        style={{ backgroundColor: '#006847' }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center bg-white bg-opacity-20"
                                style={{ width: '40px', height: '40px' }}
                            >
                                <i className="bi bi-share-fill fs-5 text-warning"></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold">Ranking para Compartir</h5>
                                <small className="opacity-75">
                                    Composiciones en alta definición (1080x1920 y 1920x1080) del Podio de Grito Mexicano
                                </small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            aria-label="Cerrar modal"
                        ></button>
                    </div>

                    {/* BARRA DE HERRAMIENTAS Y CONTROLES */}
                    <div className="bg-light p-3 px-4 border-bottom flex-shrink-0">
                        <div className="row g-3 align-items-center">
                            {/* Selector de Pestañas Formato */}
                            <div className="col-12 col-md-6">
                                <div className="btn-group w-100" role="group">
                                    <button
                                        type="button"
                                        className={`btn fw-bold d-flex align-items-center justify-content-center gap-2 ${
                                            activeTab === 'stories' ? 'btn-success text-white shadow-sm' : 'btn-outline-secondary'
                                        }`}
                                        onClick={() => setActiveTab('stories')}
                                    >
                                        <i className="bi bi-phone"></i>
                                        <span>Stories 9:16 (1080x1920)</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn fw-bold d-flex align-items-center justify-content-center gap-2 ${
                                            activeTab === 'feed' ? 'btn-success text-white shadow-sm' : 'btn-outline-secondary'
                                        }`}
                                        onClick={() => setActiveTab('feed')}
                                    >
                                        <i className="bi bi-aspect-ratio"></i>
                                        <span>Feed / Pantalla 16:9 (1920x1080)</span>
                                    </button>
                                </div>
                            </div>

                            {/* Switches de Configuración */}
                            <div className="col-12 col-md-6 d-flex flex-wrap align-items-center justify-content-md-end gap-3">
                                {/* Switch Enmascarar Nombres */}
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="switchMaskNamesSocial"
                                        checked={maskNames}
                                        onChange={(e) => setMaskNames(e.target.checked)}
                                    />
                                    <label className="form-check-label small fw-semibold text-secondary" htmlFor="switchMaskNamesSocial">
                                        {maskNames ? 'Enmascarar (Juan P.)' : 'Nombre Completo'}
                                    </label>
                                </div>

                                {/* Badge Indicador de Datos */}
                                <span className={`badge px-3 py-2 rounded-pill ${hasRealData ? 'bg-success' : 'bg-secondary'}`}>
                                    <i className={`bi ${hasRealData ? 'bi-database-check' : 'bi-inbox'} me-1`}></i>
                                    {hasRealData ? `${processedRanking.length} Registros Oficiales` : 'Sin registros aún'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* CUERPO DEL MODAL CON PREVISUALIZACIÓN */}
                    <div
                        className="card-body p-3 p-md-4 overflow-auto d-flex flex-column align-items-center justify-content-center"
                        style={{
                            backgroundColor: '#121614',
                            minHeight: '440px',
                            maxHeight: 'calc(94vh - 200px)'
                        }}
                    >
                        <div
                            className="position-relative d-flex justify-content-center align-items-center w-100"
                            style={{
                                maxHeight: '100%',
                                minHeight: '380px'
                            }}
                        >
                            {/* Canvas en Alta Definición con escalado CSS responsivo */}
                            <canvas
                                ref={canvasRef}
                                style={{
                                    maxHeight: activeTab === 'stories' ? '56vh' : '52vh',
                                    maxWidth: '100%',
                                    aspectRatio: activeTab === 'stories' ? '9 / 16' : '16 / 9',
                                    width: activeTab === 'stories' ? 'auto' : '100%',
                                    height: 'auto',
                                    borderRadius: '16px',
                                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.75)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                        <small className="text-muted mt-2">
                            <i className="bi bi-info-circle me-1"></i>
                            Renderizado en Canvas 2D nativo ({activeTab === 'stories' ? '1080 x 1920 px' : '1920 x 1080 px'}). La descarga conservará la máxima resolución sin compresión.
                        </small>
                    </div>

                    {/* PIE DE MODAL CON ACCIONES */}
                    <div className="card-footer bg-light p-3 px-4 d-flex flex-wrap justify-content-between align-items-center gap-2 border-top flex-shrink-0">
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                onClick={handleCopyToClipboard}
                                title="Copiar imagen al portapapeles"
                            >
                                <i className="bi bi-clipboard-check"></i>
                                <span className="d-none d-sm-inline">Copiar al Portapapeles</span>
                            </button>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary px-4 fw-semibold"
                                onClick={onClose}
                            >
                                Cerrar
                            </button>
                            <button
                                type="button"
                                className="btn btn-success px-4 fw-bold text-white d-flex align-items-center gap-2 shadow-sm"
                                onClick={handleDownloadPng}
                                disabled={isExporting}
                            >
                                {isExporting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                        <span>Generando PNG...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-download fs-5"></i>
                                        <span>Descargar Imagen PNG ({activeTab === 'stories' ? '9:16' : '16:9'})</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalNoisemeterSocialModal;
